import { Children, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { dialog, storage } from 'app/utils';
import { useLoader, useMountEffect } from 'app/hooks';
import { Breadcrumb } from 'app/components';
import { Button } from 'app/components/common';
import { Table, ITable, IPaging } from 'app/components/data';
import { templates } from 'app/components/utils';
import { offers } from 'app/services';
import { IOffer } from 'app/models';

const stateKey = 'modules.offers';
const loader = (params: IPaging) => {
  storage.set(stateKey, params);
  const { page, rows, sort } = params;

  return offers.query({ sort: sort?.field, order: sort?.order, skip: (page * rows), take: rows });
};

export default memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statuses: Record<number, string> = useMemo(() => ({
    0: t('app.status.rejected'),
    1: t('app.status.waiting'),
    2: t('app.status.approved'),
  }), [t]);

  const { loading, data, total, params, reload } = useLoader(loader);

  const actions: Record<string, (item: IOffer) => Promise<any> | void> = useMemo(() => ({
    delete: (item) => dialog.confirm({
      message: t('offers.delete.question', { ...item }),
      accept: { label: t('app.delete'), className: 'ml-2 p-button-danger' },
      reject: { label: t('app.cancel') },
    }).then((accepted) => {
      if (accepted) offers.delete(item).then(({ success }) => {
        if (success) dialog.toast({ severity: 'success', message: t(`offers.delete.success`, { ...item }) });

        return reload(params);
      });
    }),
  }), [params, reload, t]);

  const table: ITable<IOffer> = { data, total, loading,
    className: ['min-h-0', 'flex-auto'],
    columns: [
      { field: 'sender', type: 'string', header: t('offers.fields.sender'), className: 'p-0',
        template: ({ sender }) => templates.profile(sender),
      },
      { field: 'receiver', type: 'string', header: t('offers.fields.receiver'), className: 'p-0',
        template: ({ receiver }) => templates.profile(receiver),
      },
      { field: 'timestamp', type: 'datetime', header: { label: t('offers.fields.timestamp'), className: 'w-10rem' }, sortable: true },
      { field: 'responded', type: 'datetime', header: { label: t('offers.fields.responded'), className: 'w-10rem' }, sortable: true },
      { field: 'status', type: 'boolean', header: { label: t('offers.fields.status'), className: 'w-6rem' },
        template: (item) => statuses[item.status],
      },
      { field: 'actions', align: 'right', header: { className: 'w-6rem' }, className: 'p-0',
        template: (item) => Children.toArray([
          <Button href={`${item.id}`} text rounded icon="mdi-edit" />,
          <Button severity="danger" text rounded icon="mdi-delete" onClick={() => actions['delete'](item)} />,
        ]),
      },
    ],
    dataKey: 'id',
    removableSort: true,
    resizableColumns: true,
    scrollable: true,
    scrollHeight: 'flex',
    selectionMode: 'single',
    paginator: { show: true, page: params?.page, rows: params?.rows, options: [20, 50, 100], className: 'border-none' },
    sort: params?.sort,

    onLoad: reload,
    onRowDoubleClick: (item) => navigate(item.id),
  };

  useMountEffect(() => {
    reload(storage.get(stateKey) ?? { page: 0, rows: 20 }).then();
  });

  return (
    <div className="h-full flex flex-column">
      <div className="flex flex-none surface-200">
        <Breadcrumb className="flex-auto border-none border-noround bg-transparent" />
        <div className="flex flex-none m-2">
          <Button text icon="mdi-cached" severity="success" className="px-3 py-1" disabled={loading} onClick={() => reload(params)} />
        </div>
      </div>
      <Table { ...table } />
    </div>
  );
});
